import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/LegalPages.css';

const PolitiqueConfidentialite = () => {
  const { t } = useTranslation();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">
          ← {t('legal.backHome') || 'Retour à l\'accueil'}
        </Link>

        <h1 className="legal-title">
          {t('legal.privacy.title') || 'Politique de Confidentialité'}
        </h1>

        <div className="legal-content">
          {/* Introduction */}
          <section className="legal-section">
            <p className="legal-intro">
              La protection de vos données personnelles est une priorité pour nous. Cette politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD).
            </p>
          </section>

          {/* Responsable du traitement */}
          <section className="legal-section">
            <h2>{t('legal.privacy.responsible') || 'Responsable du traitement'}</h2>
            <p>
              <strong>Nom :</strong> Ibrahim Zaougui<br />
              <strong>Email :</strong> <a href="mailto:contact@dreamdev.fr">contact@dreamdev.fr</a><br />
              <strong>Adresse :</strong> 710 Routes de Frans - Villefranche sur Saône
            </p>
          </section>

          {/* Données collectées */}
          <section className="legal-section">
            <h2>{t('legal.privacy.collected') || 'Données personnelles collectées'}</h2>
            
            <h3>1. Via les formulaires de contact</h3>
            <p>Lorsque vous utilisez nos formulaires de contact (client ou entreprise), nous collectons :</p>
            <ul>
              <li><strong>Formulaire Client :</strong> Nom, Prénom, Email, Téléphone, Type de projet, Budget, Description du projet</li>
              <li><strong>Formulaire Entreprise :</strong> Nom, Prénom, Email, Téléphone, Nom de l'entreprise, Poste, Type de contrat (CDI/CDD/Freelance), Localisation, Salaire, Description du poste</li>
            </ul>

            <h3>2. Cookies et traceurs</h3>
            <p>Nous utilisons les cookies suivants :</p>
            <ul>
              <li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement du site (authentification, préférences de langue, consentement cookies)</li>
              <li><strong>Cookies analytiques (optionnels) :</strong> Google Analytics pour comprendre l'utilisation du site (pages visitées, durée de visite, origine du trafic)</li>
            </ul>

            <h3>3. Données de navigation</h3>
            <p>
              Nous collectons automatiquement certaines informations lors de votre navigation : adresse IP, type de navigateur, pages consultées, date et heure de visite, site référent.
            </p>
          </section>

          {/* Finalités du traitement */}
          <section className="legal-section">
            <h2>{t('legal.privacy.purpose') || 'Finalités du traitement'}</h2>
            <p>Vos données personnelles sont collectées et traitées pour :</p>
            <ul>
              <li>Répondre à vos demandes de devis ou d'opportunités professionnelles</li>
              <li>Vous contacter pour discuter de votre projet ou proposition</li>
              <li>Améliorer nos services et l'expérience utilisateur</li>
              <li>Analyser le trafic du site (si vous avez accepté les cookies analytiques)</li>
              <li>Respecter nos obligations légales</li>
            </ul>
          </section>

          {/* Base légale */}
          <section className="legal-section">
            <h2>{t('legal.privacy.legal') || 'Base légale du traitement'}</h2>
            <p>Le traitement de vos données repose sur :</p>
            <ul>
              <li><strong>Votre consentement :</strong> Lorsque vous remplissez un formulaire ou acceptez les cookies analytiques</li>
              <li><strong>L'intérêt légitime :</strong> Pour améliorer nos services et analyser l'utilisation du site</li>
              <li><strong>L'exécution d'un contrat :</strong> Pour traiter vos demandes de devis ou de collaboration</li>
            </ul>
          </section>

          {/* Destinataires des données */}
          <section className="legal-section">
            <h2>{t('legal.privacy.recipients') || 'Destinataires des données'}</h2>
            <p>Vos données personnelles sont destinées à :</p>
            <ul>
              <li>Ibrahim Zaougui en tant que développeur et responsable du traitement</li>
              <li>Prestataires techniques : hébergeur du site (OVH), service d'envoi d'emails (Node.js/Nodemailer)</li>
              <li>Outils d'analyse : Google Analytics (si vous avez accepté les cookies analytiques)</li>
            </ul>
            <p>
              Aucune donnée n'est vendue, louée ou partagée avec des tiers à des fins commerciales.
            </p>
          </section>

          {/* Durée de conservation */}
          <section className="legal-section">
            <h2>{t('legal.privacy.retention') || 'Durée de conservation'}</h2>
            <p>Vos données sont conservées pendant :</p>
            <ul>
              <li><strong>Formulaires de contact :</strong> 3 ans à compter de la dernière interaction</li>
              <li><strong>Cookies analytiques :</strong> 13 mois maximum</li>
              <li><strong>Données de connexion :</strong> 1 an (obligation légale)</li>
            </ul>
            <p>
              À l'issue de ces délais, vos données sont supprimées ou anonymisées.
            </p>
          </section>

          {/* Droits des utilisateurs */}
          <section className="legal-section">
            <h2>{t('legal.privacy.rights') || 'Vos droits'}</h2>
            <p>Conformément au RGPD, vous disposez des droits suivants :</p>
            <ul>
              <li><strong>Droit d'accès :</strong> Obtenir une copie de vos données personnelles</li>
              <li><strong>Droit de rectification :</strong> Corriger des données inexactes ou incomplètes</li>
              <li><strong>Droit à l'effacement :</strong> Demander la suppression de vos données (« droit à l'oubli »)</li>
              <li><strong>Droit d'opposition :</strong> Vous opposer au traitement de vos données</li>
              <li><strong>Droit à la limitation :</strong> Demander la limitation du traitement</li>
              <li><strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré</li>
              <li><strong>Droit de retirer votre consentement :</strong> À tout moment, sans affecter la licéité du traitement antérieur</li>
            </ul>
            <p>
              Pour exercer ces droits, contactez-nous à : <a href="mailto:contact@dreamdev.fr">contact@dreamdev.fr</a>
            </p>
            <p>
              Vous disposez également du droit d'introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
            </p>
          </section>

          {/* Sécurité */}
          <section className="legal-section">
            <h2>{t('legal.privacy.security') || 'Sécurité des données'}</h2>
            <p>
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre :
            </p>
            <ul>
              <li>La destruction accidentelle ou illicite</li>
              <li>La perte, l'altération ou la divulgation non autorisée</li>
              <li>L'accès non autorisé</li>
            </ul>
            <p>Mesures de sécurité mises en place :</p>
            <ul>
              <li>Cryptage SSL/TLS (HTTPS) pour sécuriser les échanges</li>
              <li>Hébergement sécurisé avec sauvegardes régulières</li>
              <li>Accès restreint aux données personnelles</li>
            </ul>
          </section>

          {/* Transfert de données */}
          <section className="legal-section">
            <h2>{t('legal.privacy.transfer') || 'Transfert de données hors UE'}</h2>
            <p>
              Vos données peuvent être transférées et stockées sur des serveurs situés en dehors de l'Union Européenne, notamment :
            </p>
            <ul>
              <li>Serveurs d'hébergement (France)</li>
              <li>Google Analytics (États-Unis)</li>
            </ul>
            <p>
              Ces transferts sont encadrés par des garanties appropriées conformes au RGPD (clauses contractuelles types, Privacy Shield, etc.).
            </p>
          </section>

          {/* Mineurs */}
          <section className="legal-section">
            <h2>{t('legal.privacy.minors') || 'Données des mineurs'}</h2>
            <p>
              Ce site n'est pas destiné aux personnes de moins de 16 ans. Nous ne collectons pas sciemment de données personnelles de mineurs. Si vous êtes parent et pensez que votre enfant nous a fourni des données, contactez-nous immédiatement.
            </p>
          </section>

          {/* Modifications */}
          <section className="legal-section">
            <h2>{t('legal.privacy.modifications') || 'Modifications de la politique'}</h2>
            <p>
              Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Toute modification sera publiée sur cette page avec une nouvelle date de mise à jour. Nous vous encourageons à consulter régulièrement cette page.
            </p>
          </section>

          {/* Contact */}
          <section className="legal-section">
            <h2>{t('legal.privacy.contact') || 'Contact'}</h2>
            <p>
              Pour toute question concernant cette politique de confidentialité ou le traitement de vos données personnelles :
            </p>
            <p>
              <strong>Email :</strong> <a href="mailto:contact@dreamdev.fr">contact@dreamdev.fr</a><br />
              <strong>Courrier :</strong> 710 Routes de Frans - Villefranche sur Saône
            </p>
          </section>

          {/* Date de mise à jour */}
          <section className="legal-section">
            <p className="legal-update">
              <strong>{t('legal.lastUpdate') || 'Dernière mise à jour'} :</strong> {new Date().toLocaleDateString('fr-FR')}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialite;